import { useCallback, ChangeEvent, useEffect, useState } from 'react';

import { Box, makeStyles, TextField } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import { useAppDispatch, useAppSelector } from 'store';
import { fetchTags, TagInterface, tagsSelector } from 'store/reducers/tags';

import { TagItem } from './TagItem';

type FilterTagsProps = {
  titleButton?: string;
  handleFilter: (tags: TagInterface[]) => void;
  values: TagInterface[];
};

const useStyles = makeStyles(({ typography, spacing }) => ({
  chip: {
    fontSize: typography.pxToRem(10),
    cursor: 'pointer',
  },
  chipLabel: {
    fontSize: typography.pxToRem(10),
    fontWeight: typography.fontWeightMedium as number,
    cursor: 'pointer',
    paddingLeft: spacing(1.5),
  },
  listbox: {
    scrollbarGutter: 'stable',
  },
  option: {
    fontSize: typography.pxToRem(11),
    cursor: 'pointer',
    paddingRight: 0,
    paddingLeft: spacing(1),
    borderRadius: spacing(0.5),
    marginLeft: spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginRight: 8,
  },
}));

export default function FilterTagsV2({
  handleFilter,
  values,
}: FilterTagsProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { tags, loadingTags } = useAppSelector(tagsSelector);
  const [open, setOpen] = useState(false);

  const handleChange = useCallback(
    (
      _event: ChangeEvent<Record<string, unknown>>,
      newValue: TagInterface[],
    ) => {
      handleFilter(newValue);
    },
    [handleFilter],
  );

  const handleRemoveTag = useCallback(
    (tagId?: string) => {
      if (!tagId) return;
      handleFilter(values.filter(tag => tag.id !== tagId));
    },
    [values, handleFilter],
  );

  const renderOption = useCallback(
    (option: TagInterface) => (
      <Box className={classes.row} key={option.id}>
        <TagItem tag={option} />
      </Box>
    ),
    [classes.row],
  );

  const renderTags = useCallback(
    (value: TagInterface[]) =>
      value.map((option, index) => (
        <Box
          key={option.id}
          style={{ margin: 2, marginRight: index === value.length - 1 ? 8 : 2 }}
        >
          <TagItem tag={option} onRemove={handleRemoveTag} />
        </Box>
      )),
    [handleRemoveTag],
  );

  useEffect(() => {
    if (open && !tags.length && !loadingTags) {
      dispatch(fetchTags());
    }
  }, [dispatch, loadingTags, open, tags.length]);

  return (
    <Autocomplete
      multiple
      id="tag-filter-autocomplete"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={tags}
      value={values}
      disableCloseOnSelect
      getOptionLabel={option => option.nome || ''}
      onChange={handleChange}
      renderOption={renderOption}
      renderTags={renderTags}
      popupIcon={<ExpandMore style={{ fontSize: 18 }} />}
      size="small"
      closeText="Fechar"
      openText="Abrir"
      clearText="Limpar"
      classes={{
        listbox: classes.listbox,
        option: classes.option,
      }}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label="Tags"
          placeholder="Selecione tags"
          InputProps={{
            ...params.InputProps,
            'aria-label': 'Filtro de tags',
          }}
        />
      )}
    />
  );
}
